import ApiService from "./ApiService";
import { stringify } from "querystring";
import { format } from "util";
import { paymentProcessor } from "./PaymentIntegrationService";

const ENDPOINTS = {
  PAYMENT_METHOD: "payment/",
  PAYMENT_DETAILS: "payment/details/",
  SUBSCRIBE: "subscription/",
  COUNTRIES: "payment/countries/",
  UPDATTE_BILLING_ADDRESS: "payment/change-billing-address/",
  CHANGE_CREDIT_CARD: "payment/change-credit-card/",
  TRANSACTIONS: "transaction/",
  GET_CUSTOMER_CARE_PRODUCTS: "all/products/service/?page=%s&search=%s",
  GET_ALL_SERVICES: "all/products/service/",
  SERVICE_CATALOG: "all/products/service/grouped/",
  SQUARE_CHECKOUT: "square/checkout/",
  APPOINTMENT_FEE: "square/appointment-fee/",
  CANCELLATION_FEE: "square/cancellation-fee/",
  AEVA_PRODUCTS: "aeva/product/",
  AEVA_PRODUCTS_INVOICE: "aeva/product/invoice/",
  CHECKOUT: {
    stripe: "stripe/checkout/",
    square: "square/checkout/",
  },
  STRIPE_CREATE_PAYMENT_INTENT: "payment/checkout/intent/",
  INVOICE_REFUND_DETAIL: "invoices/%s/refund-detail/",
  INVOICE_REFUND: "invoices/%s/refund/",
  INVOICE_RESEND_RECEIPT: "invoices/%s/resend-receipt/",
  INVOICE_ITEM: "invoice-items/%s/",
  // Stepper-flow checkout: terminal, saved card, and cash all post here.
  INVOICES_CHECKOUT: "invoices/checkout/",
  // Secure-link payment uses a separate endpoint (sends the link email/SMS).
  INVOICES_PAYMENT_LINK: "invoices/payment-link/",
  INVOICES_PAYMENT_LINK_NOTIFY: "invoices/payment-link/send/",
  INVOICES_PAYMENT_LINK_DETAIL: "invoices/payment-link/%s/",
  INVOICES_PAYMENT_LINK_VOID: "invoices/payment-link/%s/cancel/",
};

class PaymentService extends ApiService {
  addPaymentMethod = (data) => {
    return this.apiClient.post(ENDPOINTS.PAYMENT_METHOD, data);
  };

  getPaymentDetails = () => {
    return this.apiClient.get(ENDPOINTS.PAYMENT_DETAILS);
  };

  subscribe = (data) => {
    return this.apiClient.post(ENDPOINTS.SUBSCRIBE, data);
  };

  getCountries = () => {
    return this.apiClient.get(ENDPOINTS.COUNTRIES);
  };

  updateBillingAddress = (data) => {
    return this.apiClient.post(ENDPOINTS.UPDATTE_BILLING_ADDRESS, data);
  };

  changeCreditCard = (data) => {
    return this.apiClient.post(ENDPOINTS.CHANGE_CREDIT_CARD, data);
  };

  getTransactions = (page, perPage) => {
    const queryStringParams = {
      page,
      per_page: perPage,
    };
    return this.apiClient.get(
      `${ENDPOINTS.TRANSACTIONS}?${stringify(queryStringParams)}`
    );
  };

  getCustomerCareProducts = (page, search) => {
    return this.apiClient.get(
      format(ENDPOINTS.GET_CUSTOMER_CARE_PRODUCTS, page, search)
    );
  };

  getServices = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_ALL_SERVICES}?${stringify(payload)}`
    );
  };

  getServiceCatalog = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.SERVICE_CATALOG}?${stringify(payload)}`,
    );
  };

  squareCheckout = (payload) => {
    return this.apiClient.post(ENDPOINTS.CHECKOUT[paymentProcessor()], payload);
  };

  invoiceCheckout = (payload) => {
    return this.apiClient.post(ENDPOINTS.INVOICES_CHECKOUT, payload);
  };

  sendInvoiceLink = (payload) => {
    return this.apiClient.post(ENDPOINTS.INVOICES_PAYMENT_LINK, payload);
  };

  sendPaymentLinkNotification = (payload) => {
    return this.apiClient.post(ENDPOINTS.INVOICES_PAYMENT_LINK_NOTIFY, payload);
  };

  voidPaymentLink = (id, payload = {}) => {
    return this.apiClient.post(
      format(ENDPOINTS.INVOICES_PAYMENT_LINK_VOID, id),
      payload
    );
  };

  getPaymentLink = (id) => {
    return this.apiClient.get(
      format(ENDPOINTS.INVOICES_PAYMENT_LINK_DETAIL, id)
    );
  };

  getPaymentLinks = (params) => {
    return this.apiClient.get(
      `${ENDPOINTS.INVOICES_PAYMENT_LINK}?${stringify(params)}`
    );
  };

  chargeAppointmentFee = (payload) => {
    return this.apiClient.post(ENDPOINTS.APPOINTMENT_FEE, payload);
  };

  chargeCancellationFee = (payload) => {
    return this.apiClient.post(ENDPOINTS.CANCELLATION_FEE, payload);
  };

  getAevaProducts = () => {
    return this.apiClient.get(ENDPOINTS.AEVA_PRODUCTS);
  };

  buyAevaProduct = (payload) => {
    return this.apiClient.post(ENDPOINTS.AEVA_PRODUCTS_INVOICE, payload);
  };

  getAevaProductInvoices = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.AEVA_PRODUCTS_INVOICE}?${stringify(payload)}`
    );
  };

  createStripePaymentIntent = (payload) => {
    return this.apiClient.post(ENDPOINTS.STRIPE_CREATE_PAYMENT_INTENT, payload);
  };

  getInvoiceRefundDetail = (invoiceId) => {
    return this.apiClient.get(
      format(ENDPOINTS.INVOICE_REFUND_DETAIL, invoiceId)
    );
  };

  createInvoiceRefund = (invoiceId, payload) => {
    return this.apiClient.post(
      format(ENDPOINTS.INVOICE_REFUND, invoiceId),
      payload
    );
  };

  resendInvoiceReceipt = (invoiceId) => {
    return this.apiClient.post(
      format(ENDPOINTS.INVOICE_RESEND_RECEIPT, invoiceId)
    );
  };

  updateInvoiceItem = (itemId, payload) => {
    return this.apiClient.patch(
      format(ENDPOINTS.INVOICE_ITEM, itemId),
      payload
    );
  };
}

export default new PaymentService();
